import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, Package, Eye, Download, Search, Filter, LayoutGrid, TableIcon, ChevronDown, ChevronUp, Lock, LockOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';
import { toast } from 'sonner';
import { exportReportsToExcel } from '../utils/exportUtils';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

export const ReportsListPage = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusGroups, setStatusGroups] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban'
  const [closedExpanded, setClosedExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
    fetchFilters();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/reports');
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      toast.error('Chyba při načítání hlášení');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [categoriesRes, statusesRes] = await Promise.all([
        api.get('/categories'),
        api.get('/statuses')
      ]);
      setCategories(categoriesRes.data);
      setStatuses(statusesRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  // Calculate status groups based on ALL reports (not filtered)
  useEffect(() => {
    const groups = {};
    statuses.forEach(status => {
      groups[status.id] = reports.filter(r => r.status_id === status.id);
    });
    setStatusGroups(groups);
  }, [reports, statuses]);

  // Filter reports whenever search or filters change
  useEffect(() => {
    let filtered = [...reports];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(report =>
        report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.batch.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(report => report.category_id === selectedCategory);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(report => report.status_id === selectedStatus);
    }

    setFilteredReports(filtered);
  }, [searchQuery, selectedCategory, selectedStatus, reports]);

  const handleExport = async () => {
    if (filteredReports.length === 0) {
      toast.error('Žádná data k exportu');
      return;
    }
    exportReportsToExcel(filteredReports);
    toast.success('Excel export dokončen');
  };

  const handleStatusChange = async (reportId, newStatusId) => {
    try {
      await api.put(`/reports/${reportId}`, { status_id: newStatusId });
      toast.success('Stav hlášení aktualizován');
      fetchReports();
    } catch (error) {
      toast.error('Chyba při změně stavu');
      fetchReports(); // Reload to revert UI
    }
  };

  const handleDragEnd = (result) => {
    const { draggableId, source, destination } = result;

    // Dropped outside
    if (!destination) return;

    // Dropped in same column
    if (source.droppableId === destination.droppableId) return;

    // Change status
    const reportId = draggableId;
    const newStatusId = destination.droppableId;

    handleStatusChange(reportId, newStatusId);
  };

  const handleToggleClosed = async (e, reportId) => {
    e.stopPropagation();
    try {
      await api.patch(`/reports/${reportId}/toggle-closed`);
      toast.success('Stav hlášení aktualizován');
      fetchReports();
    } catch (error) {
      toast.error('Chyba při změně stavu');
    }
  };

  // Separate active and closed reports
  const activeReports = filteredReports.filter(r => !r.is_closed);
  const closedReports = filteredReports.filter(r => r.is_closed);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="reports-list-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl mb-2">Hlášení</h1>
            <p className="text-lg text-slate-600">Přehled všech hlášení o zmetkovité výrobě</p>
          </div>
          <div className="flex gap-3">
            <div className="flex border border-cyan-200 rounded-lg p-1 bg-white">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                  viewMode === 'table'
                    ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-mode-table"
              >
                <TableIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Tabulka</span>
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all ${
                  viewMode === 'kanban'
                    ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="view-mode-kanban"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="text-sm font-medium">Kanban</span>
              </button>
            </div>
            <Button
              onClick={handleExport}
              variant="outline"
              data-testid="export-button"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportovat Excel
            </Button>
          </div>
        </div>

        {/* Status Quick Filters */}
        <div className="mb-6">
          <div className="flex gap-3 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedStatus('all')}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all whitespace-nowrap ${
                selectedStatus === 'all'
                  ? 'border-cyan-500 bg-cyan-50 shadow-md'
                  : 'border-slate-200 bg-white hover:border-cyan-300 hover:shadow-sm'
              }`}
              data-testid="status-filter-all"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500" />
                <span className={`font-semibold ${selectedStatus === 'all' ? 'text-cyan-700' : 'text-slate-700'}`}>
                  Všechna hlášení
                </span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${
                selectedStatus === 'all' ? 'bg-cyan-600 text-white' : 'bg-slate-200 text-slate-700'
              }`}>
                {reports.length}
              </span>
            </button>

            {statuses.map((status) => {
              const count = statusGroups[status.id]?.length || 0;
              const isActive = selectedStatus === status.id;
              
              return (
                <button
                  key={status.id}
                  onClick={() => setSelectedStatus(status.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all whitespace-nowrap ${
                    isActive
                      ? 'border-opacity-100 shadow-md scale-105'
                      : 'border-slate-200 bg-white hover:shadow-sm hover:scale-102'
                  }`}
                  style={{
                    borderColor: isActive ? status.color : undefined,
                    backgroundColor: isActive ? `${status.color}15` : undefined
                  }}
                  data-testid={`status-filter-${status.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className={`font-semibold ${isActive ? 'text-slate-900' : 'text-slate-700'}`}>
                      {status.name}
                    </span>
                  </div>
                  <span 
                    className={`px-2 py-0.5 rounded-full text-sm font-bold text-white`}
                    style={{ backgroundColor: status.color }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="bg-white rounded-xl border border-cyan-200 p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Pokročilé filtry:</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cyan-400" />
              <Input
                placeholder="Hledat podle názvu nebo šarže..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Všechny kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny kategorie</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 text-sm text-slate-600">
            Zobrazeno: <span className="font-medium text-cyan-700">{filteredReports.length}</span> z {reports.length} hlášení
          </div>
        </div>

        {/* Reports List */}
        {filteredReports.length === 0 && reports.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Zatím žádná hlášení</h3>
            <p className="text-slate-600 mb-6">Začněte vytvořením nového hlášení</p>
            <Button
              onClick={() => navigate('/reports/new')}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="create-first-report-button"
            >
              Vytvořit hlášení
            </Button>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="bg-white rounded-xl border border-cyan-200 p-12 text-center">
            <Filter className="h-12 w-12 text-cyan-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Žádné výsledky</h3>
            <p className="text-slate-600 mb-6">Zkuste změnit filtry nebo vyhledávací dotaz</p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedStatus('all');
              }}
              variant="outline"
              data-testid="reset-filters-button"
            >
              Obnovit filtry
            </Button>
          </div>
        ) : viewMode === 'kanban' ? (
          /* Kanban View - max 3 columns per row */
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kanban-board">
              {statuses.map((status) => {
                const statusReports = statusGroups[status.id] || [];
                
                return (
                  <div 
                    key={status.id} 
                    className="w-full"
                    data-testid={`kanban-column-${status.id}`}
                  >
                    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
                      {/* Column Header */}
                      <div 
                        className="p-4 border-b-2 flex items-center justify-between"
                        style={{ borderColor: status.color, backgroundColor: `${status.color}10` }}
                      >
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.color }}
                          />
                          <h3 className="font-semibold text-slate-900">{status.name}</h3>
                        </div>
                        <span 
                          className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: status.color }}
                        >
                          {statusReports.length}
                        </span>
                      </div>

                      {/* Column Content */}
                      <Droppable droppableId={status.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`p-3 min-h-[200px] max-h-[600px] overflow-y-auto ${
                              snapshot.isDraggingOver ? 'bg-cyan-50' : ''
                            }`}
                          >
                            {statusReports.length === 0 ? (
                              <div className="text-center py-8 text-slate-400 text-sm">
                                Žádná hlášení
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {statusReports.map((report, index) => (
                                  <Draggable 
                                    key={report.id} 
                                    draggableId={report.id} 
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`bg-white border border-slate-200 rounded-lg p-3 cursor-move transition-shadow ${
                                          snapshot.isDragging ? 'shadow-lg border-cyan-300' : 'hover:shadow-md'
                                        }`}
                                        onClick={() => navigate(`/reports/${report.id}`)}
                                        data-testid={`kanban-card-${report.id}`}
                                      >
                                        <h4 className="font-medium text-slate-900 mb-2 line-clamp-2">
                                          {report.title}
                                        </h4>
                                        <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                                          <Package className="h-3 w-3" />
                                          {report.batch}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                                          <Calendar className="h-3 w-3" />
                                          {format(new Date(report.created_at), 'dd.MM.yyyy', { locale: cs })}
                                        </div>
                                        {report.category_name && (
                                          <Badge 
                                            variant="outline" 
                                            className="text-xs bg-cyan-50 text-cyan-700 border-cyan-200"
                                          >
                                            {report.category_name}
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        ) : (
          /* Table View */
          <div className="space-y-6">
            {/* Active Reports */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <LockOpen className="h-4 w-4 text-cyan-600" />
                  <span className="font-semibold text-slate-700">Aktivní hlášení</span>
                  <Badge className="bg-cyan-100 text-cyan-700">{activeReports.length}</Badge>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Název
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Šarže
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Počet kusů
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Kategorie
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Stav
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Vytvořeno
                      </th>
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                        Akce
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeReports.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-slate-500">
                          Žádná aktivní hlášení
                        </td>
                      </tr>
                    ) : (
                      activeReports.map((report) => (
                        <tr
                          key={report.id}
                          data-testid={`report-row-${report.id}`}
                          className="border-b border-slate-100 transition-colors hover:bg-slate-50/50 cursor-pointer"
                          onClick={() => navigate(`/reports/${report.id}`)}
                        >
                          <td className="p-4 align-middle">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span className="font-medium text-slate-900">{report.title}</span>
                            </div>
                          </td>
                          <td className="p-4 align-middle text-sm text-slate-700">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-slate-400" />
                              {report.batch}
                            </div>
                          </td>
                          <td className="p-4 align-middle text-sm text-slate-700">
                            {report.quantity}
                          </td>
                          <td className="p-4 align-middle text-sm text-slate-700">
                            {report.category_name || 'Bez kategorie'}
                          </td>
                          <td className="p-4 align-middle">
                            <Badge
                              style={{ backgroundColor: report.status_color + '20', color: report.status_color }}
                              className="font-semibold"
                              data-testid={`status-badge-${report.id}`}
                            >
                              {report.status_name}
                            </Badge>
                          </td>
                          <td className="p-4 align-middle text-sm text-slate-700">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              {format(new Date(report.created_at), 'dd.MM.yyyy', { locale: cs })}
                            </div>
                          </td>
                          <td className="p-4 align-middle">
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => handleToggleClosed(e, report.id)}
                                title="Uzavřít hlášení"
                                data-testid={`close-report-${report.id}`}
                              >
                                <Lock className="h-4 w-4 text-slate-400" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/reports/${report.id}`);
                                }}
                                data-testid={`view-report-${report.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Closed Reports - Collapsible */}
            {closedReports.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setClosedExpanded(!closedExpanded)}
                  className="w-full bg-slate-100 px-4 py-3 border-b border-slate-200 flex items-center justify-between hover:bg-slate-150 transition-colors"
                  data-testid="toggle-closed-section"
                >
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-slate-500" />
                    <span className="font-semibold text-slate-600">Uzavřená hlášení</span>
                    <Badge className="bg-slate-200 text-slate-600">{closedReports.length}</Badge>
                  </div>
                  {closedExpanded ? (
                    <ChevronUp className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                </button>
                
                {closedExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Název
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Šarže
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Počet kusů
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Kategorie
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Stav
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Vytvořeno
                          </th>
                          <th className="h-12 px-4 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider">
                            Akce
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {closedReports.map((report) => (
                          <tr
                            key={report.id}
                            data-testid={`report-row-closed-${report.id}`}
                            className="border-b border-slate-100 transition-colors hover:bg-slate-50/50 cursor-pointer bg-slate-50/30 opacity-70"
                            onClick={() => navigate(`/reports/${report.id}`)}
                          >
                            <td className="p-4 align-middle">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-slate-300" />
                                <span className="font-medium text-slate-600">{report.title}</span>
                              </div>
                            </td>
                            <td className="p-4 align-middle text-sm text-slate-500">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 text-slate-300" />
                                {report.batch}
                              </div>
                            </td>
                            <td className="p-4 align-middle text-sm text-slate-500">
                              {report.quantity}
                            </td>
                            <td className="p-4 align-middle text-sm text-slate-500">
                              {report.category_name || 'Bez kategorie'}
                            </td>
                            <td className="p-4 align-middle">
                              <Badge
                                style={{ backgroundColor: report.status_color + '15', color: report.status_color, opacity: 0.7 }}
                                className="font-semibold"
                              >
                                {report.status_name}
                              </Badge>
                            </td>
                            <td className="p-4 align-middle text-sm text-slate-500">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-slate-300" />
                                {format(new Date(report.created_at), 'dd.MM.yyyy', { locale: cs })}
                              </div>
                            </td>
                            <td className="p-4 align-middle">
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => handleToggleClosed(e, report.id)}
                                  title="Znovu otevřít"
                                  data-testid={`reopen-report-${report.id}`}
                                >
                                  <LockOpen className="h-4 w-4 text-slate-400" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/reports/${report.id}`);
                                  }}
                                >
                                  <Eye className="h-4 w-4 text-slate-400" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};